/**
 * Minimal typings for the Telegram WebApp object injected by
 * https://telegram.org/js/telegram-web-app.js
 *
 * Full reference: https://core.telegram.org/bots/webapps#initializing-mini-apps
 */

import type { ThemePreference } from '@ielts-buddy/shared'

export interface TelegramWebApp {
  /** Client version string, e.g. `"6.0"` or `"7.10"` */
  version?: string
  initData: string
  initDataUnsafe: {
    user?: {
      id: number
      first_name: string
      last_name?: string
      username?: string
      language_code?: string
      photo_url?: string
    }
    auth_date?: number
    hash?: string
  }
  colorScheme: 'light' | 'dark'
  themeParams: Record<string, string>
  isExpanded: boolean
  viewportHeight: number
  ready(): void
  expand(): void
  close(): void
  setHeaderColor?(color: string): void
  setBackgroundColor?(color: string): void
  showConfirm?(message: string, callback?: (confirmed: boolean) => void): void
  onEvent?(eventType: string, callback: () => void): void
  offEvent?(eventType: string, callback: () => void): void
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
    notificationOccurred(type: 'error' | 'success' | 'warning'): void
    selectionChanged(): void
  }
  MainButton: {
    text: string
    show(): void
    hide(): void
    onClick(cb: () => void): void
    offClick(cb: () => void): void
    setText(text: string): void
    enable(): void
    disable(): void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

export const tg: TelegramWebApp | undefined =
  typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined

export function isInsideTelegram(): boolean {
  return Boolean(tg?.initData && tg.initData.length > 0)
}

/** Raw WebApp `initData` for server validation (e.g. profile / photo sync). */
export function getMiniAppInitData(): string | null {
  const raw = tg?.initData?.trim()
  return raw && raw.length > 0 ? raw : null
}

// ---------------------------------------------------------------------------
// Theme bridge between Telegram and our brand palette
// ---------------------------------------------------------------------------

const BRAND_BG_LIGHT = '#F4FBF1'
const BRAND_BG_DARK = '#011A1D'

function resolveDarkMode(): boolean {
  if (typeof window !== 'undefined') {
    const forced = new URLSearchParams(window.location.search).get('theme')
    if (forced === 'dark') return true
    if (forced === 'light') return false
  }
  if (tg?.colorScheme === 'dark') return true
  if (tg?.colorScheme === 'light') return false
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  return false
}

function isDarkFromPreference(pref: ThemePreference): boolean {
  if (pref === 'dark') return true
  if (pref === 'light') return false
  return resolveDarkMode()
}

function telegramWebAppVersionAtLeast(
  minMajor: number,
  minMinor: number,
): boolean {
  const raw = tg?.version?.trim()
  if (!raw) return false
  const parts = raw.replace(/^v/i, '').split(/[.\s]+/)
  const major = Number.parseInt(parts[0] ?? '', 10)
  const minor = Number.parseInt(parts[1] ?? '0', 10)
  if (Number.isNaN(major)) return false
  const m = Number.isNaN(minor) ? 0 : minor
  if (major > minMajor) return true
  if (major < minMajor) return false
  return m >= minMinor
}

function telegramChromeColorsSupported(): boolean {
  return telegramWebAppVersionAtLeast(6, 1)
}

/**
 * Push the current brand theme into the DOM and Telegram chrome (6.1+).
 */
export function applyAppTheme(pref: ThemePreference = 'match_telegram'): void {
  if (typeof document === 'undefined') return

  const isDark = isDarkFromPreference(pref)
  const root = document.documentElement
  if (isDark) {
    root.dataset.theme = 'dark'
  } else {
    delete root.dataset.theme
  }

  if (!telegramChromeColorsSupported()) return

  const bg = isDark ? BRAND_BG_DARK : BRAND_BG_LIGHT
  try {
    tg?.setHeaderColor?.(bg)
    tg?.setBackgroundColor?.(bg)
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Haptic feedback
// ---------------------------------------------------------------------------

function telegramHapticsSupported(): boolean {
  return telegramWebAppVersionAtLeast(6, 1)
}

export const haptics = {
  light(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.impactOccurred('light')
    } catch {
      /* noop */
    }
  },
  medium(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.impactOccurred('medium')
    } catch {
      /* noop */
    }
  },
  heavy(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.impactOccurred('heavy')
    } catch {
      /* noop */
    }
  },
  success(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.notificationOccurred('success')
    } catch {
      /* noop */
    }
  },
  warning(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.notificationOccurred('warning')
    } catch {
      /* noop */
    }
  },
  error(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.notificationOccurred('error')
    } catch {
      /* noop */
    }
  },
  select(): void {
    if (!telegramHapticsSupported()) return
    try {
      tg?.HapticFeedback?.selectionChanged()
    } catch {
      /* noop */
    }
  },
}

export function showConfirm(message: string): Promise<boolean> {
  if (tg?.showConfirm) {
    return new Promise<boolean>((resolve) => {
      try {
        tg.showConfirm?.(message, (ok) => resolve(Boolean(ok)))
      } catch {
        resolve(typeof window !== 'undefined' ? window.confirm(message) : false)
      }
    })
  }
  if (typeof window !== 'undefined' && window.confirm) {
    return Promise.resolve(window.confirm(message))
  }
  return Promise.resolve(true)
}

export function watchAppTheme(onChange: () => void): () => void {
  if (tg?.onEvent && tg?.offEvent) {
    tg.onEvent('themeChanged', onChange)
    return () => tg.offEvent?.('themeChanged', onChange)
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }
  return () => {}
}
